<template>
    <div class="pt-2">
        <div class="relative w-full px-4 py-4 mb-4 border border-gray-300 bg-gray-100">
            <form @submit.prevent="update">
                <h2 class="text-green-600 text-2xl leading-10 pr-8">{{ selectedCourse?.programme_title ?? 'Select Programme' }}</h2>
                <p class="text-xl leading-10 pr-8 mb-2">{{ selectedProgramme?.moa_name ?? 'Select Mode of Study' }}</p>
                <div class="flex flex-wrap">
                    <div class="w-full">
                        <label>Programme title:</label>
                        <select v-model="selectedCourse" class="form-select mb-8 w-full bg-white" @change="changeCourse($event)">
                            <option v-for="programme in filteredCourses" :key="programme.reference" :value="programme">
                                {{ programme['programme_title'] ?? programme['sits_ipp'] }}
                            </option>
                        </select>
                    </div>
                    <div v-if="selectedCourse" class="w-full">
                        <label>Mode of study and start date:</label>
                        <select v-model="selectedProgramme" class="form-select mb-8 w-full bg-white" @change="changeProgramme($event)">
                            <option v-for="mode in filteredProgrammes" :key="mode.reference" :value="mode">
                                {{ mode['moa_name'] }} ({{ mode['moa_start_date'] }})
                            </option>
                        </select>
                    </div>

                    <div v-if="proposedStartDateShow" class="w-full flex flex-wrap">
                        <div class="w-full md:w-1/4 pr-4">
                            <label>* Proposed start date:</label>
                            <select v-if="proposedStartDateShow" v-model="proposedStartDate" class="form-select mb-8 w-full bg-white">
                                <option v-for="startDate in proposedStartDates" :key="startDate.date" :value="startDate.date">
                                    {{ startDate.date }}
                                </option>
                            </select>
                        </div>

                        <div class="w-full md:w-1/4">
                            <text-input v-model="proposedResearchTopic" :width="'full'" label="* Proposed research topic" @update:modelValue="value = $event" />
                            <label class="text-sm text-black-400">If you are applying for the Joint PhD in Global Health, you may provide details of up to two project titles.</label>
                        </div>

                        <div class="w-full md:w-1/4">
                            <text-input v-model="proposedSupervisor" :width="'full'" class="-mb-5" label="Proposed Supervisor" @update:modelValue="value = $event" />
                            <label class="text-sm text-black-400">(If you have already contacted an academic member of School staff who has expressed an interest in being your Supervisor, please give their name and Department and/or Faculty, if known.)</label>
                        </div>

                        <div class="w-full md:w-1/4">
                            <div class=" flex flex-wrap w-full">
                                <label class="mt-8 md:mt-0 w-full mb-2">* Are you an MRC Unit staff member?</label>
                                <div class="w-1/4 mr-3">
                                    <input id="1" :checked="mrcStaff===1" type="radio" value="1" name="mrcStaff" @input="mrcStaff = $event.target.value" />
                                    <label>Yes</label>
                                </div>
                                <div class="w-1/4 ml-3">
                                    <input id="0" :checked="mrcStaff===0" type="radio" value="0" name="mrcStaff" @input="mrcStaff = $event.target.value" />
                                    <label>No</label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="moduleFormShow" class="w-full">
                        <div class="w-full flex flex-wrap">
                            <label class="md:w-3/4">* Are you a current LSHTM distance learning student?</label>
                            <select v-model="hasMscDL" class="form-select md:w-1/4 mb-2 bg-white">
                                <option :value="true">Yes</option>
                                <option :value="false">No</option>
                            </select>
                        </div>

                        <div v-if="hasMscDL" class="w-full">
                            <label class="md:w-3/4">Before you submit your application for Blended Learning Modules please make sure you carefully read the guidance notes that are available on the DL General Zone, listed by programme:</label>
                            <label class="md:w-3/4"><a href="https://ble.lshtm.ac.uk/course/view.php?id=5018" target="_blank"> DL General Zone | LSHTM Moodle</a></label>
                            
                            <label class="md:w-3/4">It is important to read the Module Specification for the module/s you are interested in studying. Some modules may require you to have background knowledge in the relevant subject. You must check any prerequisites required for any module you may wish to study.</label>
                            <label class="md:w-3/4">A maximum of <b>two on-campus</b> modules may be chosen for 2024-25</label>
                            <label class="md:w-3/4">Make sure you have not already registered for the maximum number of electives for your DL programme.</label>
                            <label class="md:w-3/4">Are you eligible to progress to elective modules (as per your exam recommendation)?</label>
                            <label class="md:w-3/4">Have you checked if the blended learning module has a DL equivalent? You cannot study for both a DL module and its on-campus equivalent module.</label>
                            
                            <label class="md:w-3/4">Tell us about your Distance Learning studies to date. Please include grades for the modules you have already completed, or note if you are continuing: </label>
                        </div>
                        
                        <div v-if="hasMscDL" class="w-full flex flex-wrap mb-2">
                            <label class="md:w-3/4">* Distance learning student ID number:</label>
                            <input v-model="studentID" type="text" class="form-input md:w-1/4 mb-2 bg-white" @update:modelValue="value = $event" />
                        </div>

                        <div v-if="hasMscDL" class="w-full flex flex-wrap">
                            <label class="md:w-3/4">* Distance learning programme that you are studying:</label>
                            <select v-model="mscDLCourse" class="form-select md:w-1/4 mb-2 bg-white">
                                <option value="MSc Clinical Trials">MSc Clinical Trials</option>
                                <option value="MSc Epidemiology">MSc Epidemiology</option>
                                <option value="MSc Global Health Policy">MSc Global Health Policy</option>
                                <option value="MSc Health in Humanitarian Crises">MSc Health in Humanitarian Crises</option>
                                <option value="MSc Infectious Diseases">MSc Infectious Diseases</option>
                                <option value="MSc Public Health">MSc Public Health</option>
                            </select>
                        </div>
                        
                        <div v-if="hasMscDL" class="wide-box flex flex-wrap">
                            <label class="w-full">Please list all distance learning modules that you have registered for, and grades achieved, or leave the grade blank if you have not yet completed the module:</label>
                            <div class="w-full max-w-4xl mx-auto p-12">
                                <!-- <module-details-form
                                    v-for="(mod, index) in module"
                                    :key="mod.id"
                                    :module-data="mod"
                                    :item-index="index"
                                    @delete="removeOne"
                                    @update-module="handleModuleUpdate"
                                />-->
                                <button class="btn-green ml-8 mt-8 mb-8 items-center" type="button" @click.prevent="newModule">
                                    Add New Module
                                </button>
                                
                                
                                <table class="table-auto w-full mb-4 border-collapse border border-gray-400">
                                    <thead>
                                        <tr>
                                            <th class="px-4 py-2 border border-gray-300">Module Name and Code</th>
                                            <th class="px-4 py-2 border border-gray-300">Grade (if available)</th>
                                            <th class="px-4 py-2 border border-gray-300">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <module-details-form
                                            v-for="(mod, index) in module"
                                            :key="mod.id"
                                            :module-data="mod"
                                            :item-index="index"
                                            @delete="removeOne"
                                            @update-module="handleModuleUpdate"
                                        />
                                        
                                    </tbody>
                                </table>
                                
                            </div>
                        </div>
                        <!--<div v-if="hasMscDL" class="w-full flex flex-wrap">
                            <label class="w-full">Please tell us which Distance Learning modules you have already completed and the grades obtained.</label>
                            <textarea-input v-model="completedMscDL" class="w-full mb-2 bg-white" @update:modelValue="value = $event" />
                        </div>-->
                    </div>
                    <div v-if="successMessage" class="success-message mt-2">{{ successMessage }}</div>
                    <div v-if="serverError" class="server-error w-full flex flex-wrap">
                        <ul v-if="serverError.hasOwnProperty('errors')">
                            <li v-for="(errors, key) in serverError.errors" :key="key">
                                <p v-for="(error, index) in errors" :key="index">{{ error }}</p>
                            </li>
                        </ul>
                        <div v-else>{{ serverError }}</div>
                    </div>
                </div>
                <div class="w-1/2 pb-2 mt-0 lg:-mt-2 w-full lg:w-1/2 text-right">
                    <div>
                        <application-component-save-button :disabled="true" class="relative w-full lg:w-2/3 flex justify-center btn-green ml-auto mt-8 md:inline-block text-center"
                                                           type="submit"
                        >
                            Save Programme Details
                            <div v-if="loading" class="lds-ring-container absolute top-2">
                                <div class="lds-ring">
                                    <div />
                                    <div />
                                    <div />
                                    <div />
                                </div>
                            </div>
                        </application-component-save-button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import TextInput from '@/shared/TextInput'
import ApplicationComponentSaveButton from '@/shared/ApplicationComponentSaveButton'
import ModuleDetailsForm from '@/views/Application/Details/ModuleDetailsForm'

export default {
  components: {
    ApplicationComponentSaveButton,
    ModuleDetailsForm,
    TextInput,
  },
  props: {
    chosenProgrammeType : String,
    academicYear : String,
    modeOfAttendance: Object,
    programmesList: Object,
    coursesList: Array,
    proposedStartDates: Object,
  },
  data() {
    return {
      selectedProgrammeMode: '',
      selectedProgramme: null,
      selectedCourse: null,
      filteredCourses: [],
      filteredProgrammes: [],
      proposedStartDate: '',
      proposedResearchTopic: '',
      proposedSupervisor: '',
      mrcStaff: '',
      studentID: '',
      hasMscDL: '',
      mscDLCourse: '',
      completedMscDL: '',
      proposedStartDateShow: false,
      moduleFormShow: false,
      form: {},
      formDirty: false,
      loading: false,
      successMessage: '',
      serverError: '',
      applicationRef: this.$route.params.id,
      module: [],
    }
  },
  mounted() {
      this.changeType({})
      this.newModule()
  },
  methods: {
    removeOne(indexToRemove) {
      this.module.splice(indexToRemove, 1)
    },
    changeProgramme(event) {
      this.$emit('programmeChange', event.target.value)
    },
    changeCourse() {
      const filters = [
        {type: 'programme_type_sits_code', name: this.chosenProgrammeType},
        {type: 'programme_id', name: this.selectedCourse.reference},
        {type: 'sits_ipo_ayrc', name: this.academicYear},
      ]
      this.filteredProgrammes = this.programmesList.filter(el => filters.every(filterEl => {
        if(filterEl.name !== null && filterEl.name !== '' ) {
          return el[filterEl.type] === filterEl.name
        } else {
          return true
        }
      }))
    },
    changeType() {
      const programmeType = this.chosenProgrammeType
      if (programmeType === 'R') {
        this.proposedStartDateShow = true
      }
      else if (programmeType === 'U') {
        this.moduleFormShow = true
      }
      else {
        this.proposedStartDateShow = false
        this.proposedStartDate = null
        this.proposedResearchTopic = null
        this.proposedSupervisor = null
        this.mrcStaff = null

        this.moduleFormShow = false
        this.hasMscDL = null
        this.mscDLCourse = ''
        this.studentID = null
      }

      const filters = [
        {type: 'programme_type_sits_code', name: programmeType},
      ]
      this.filteredCourses = this.coursesList.filter(el => filters.every(filterEl => {
        if(filterEl.name !== null && filterEl.name !== '' ) {
          return el[filterEl.type] === filterEl.name
        } else {
          return true
        }
      }))
      this.$emit('typeChanged', programmeType)
    },
    update() {
      this.$store.dispatch('updateProgrammeInstance', {
        selectedProgramme: this.selectedProgramme.reference,
        proposedStartDate: this.proposedStartDate,
        proposedResearchTopic: this.proposedResearchTopic,
        proposedSupervisor: this.proposedSupervisor,
        mrcStaff: this.mrcStaff,
        hasMscDL: this.hasMscDL,
        mscDLCourse: this.mscDLCourse,
        studentID:this.studentID,
        modules: this.module,
        applicationRef: this.applicationRef,
      })
          .then(response => {
            this.loading = false
            this.serverError = ''
            this.successMessage = response.data
            this.$emit('refreshApplication', true)
            if (this.$route.path.includes('EditChoices')) {
              this.$router.push({ path: '/applications', query: { successMessage: this.successMessage } })
            } else {
              window.location.reload()
            }
          })
          .catch(error => {
            this.loading = false
            this.serverError = error.response.data
            this.successMessage = ''
          })
      this.formDirty = false
    },
    dirtyForm() {
      this.formDirty = true
      this.successMessage = ''
    },
    cancelForm () {
      this.$emit('cancelForm')
    },
    newModule() {
      const uniqueTimeId = Date.now()
      this.module.push({
        id: uniqueTimeId,  
        moduleName: '',
        grade: '', 
      })
    },
    handleModuleUpdate(payload) {
      this.module[payload.index].moduleName = payload.moduleName
      this.module[payload.index].grade = payload.grade
    },
  },
}
</script>
